<template>
    <div v-if="openRowType=='detailsPage'" class="w-full h-full">
        <div v-show="openRow!=null" class="w-full">
            <slot name="details-page"
                :resultRow="openRow"
                :userPermissions="userPermissions"
                :fetchFilter="fetchFilter"
                :events="{ closeDetails, refetchResults }"
            ></slot>
        </div>
        <div v-show="openRow==null" class="w-full h-full flex flex-col">
            <div class="w-full z-30">
                <CustomTableMenu
                    :moduleName="moduleName"
                    :module-name-singular="moduleNameSingular"
                    :pageFilter="pageSizeFilter"
                    :page="page"
                    :numPages="totalPages"
                    :isLoading="isLoading"
                    :filters="filters"
                    :selectAll="selectAll"
                    :selectedResults="selectedResults"
                    :menuOptions="menuOptions"
                    :userPermissions="userPermissions"
                    :bulkOperations="bulkOperations"
                    :viewingFromNbr="viewingFromNbr"
                    :viewingToNbr="viewingToNbr"
                    :default-page-size="defaultPageSize"
                    :count="count"
                    :orderBy="orderBy"
                    :create-modal-size="createModalSize"
                    :activeFilters="activeFilters"
                    :has-next="!!next"
                    :has-prev="!!prev"
                    :count-is-capped="countIsCapped"
                    :is-past-end="isPastEnd"
                    @refetch-results="refetchResults(fetchFilter)"
                    @get-next="getNext"
                    @go-to-first="goToFirst"
                    @get-prev="getPrev"
                    @go-to-last="goToLast"
                    @jump-to-page="jumpToPage"
                    @set-page-size-filter="setPageSizeFilter"
                    @clear-filter="clearFilter"
                    @change-filters="changeFilters"
                    @set-filter-options="setFilterOptions"
                    @toggle-select-all="toggleSelectAll"
                    @clear-select-all="clearSelectAll"
                    @add-form-row="addFormRow"
                    @change-ordering="setOrder"
                    @do-quick-bulk="executeQuickBulkAction"
                    @select-tab="selectTab"
                    @toggle-bulk-operations-panel="toggleBulkOperationsPanel"
                    @toggle-control-panel="toggleControlPanel"
                    @change-active-filters="updateActiveFilters"
                    @clear-all-active-filters="clearAllActiveFilters"
                    @export-to-csv="exportToCSV"
                >
                    <template #bulk-operations="{ events: { hideBulkActionMenu, hideBulkOperations },  bulkAction, selectedResults }">
                        <slot name="bulk-operations"
                            :selectedResults="selectedResults"
                            :bulkAction="bulkAction"
                            :fetchFilter="fetchFilter"
                            :events="{ hideBulkActionMenu, hideBulkOperations, clearSelectAllClearSelectedResults, refetchResults }"
                        ></slot>
                    </template>
                    <template #create-form="{ events: { closeCreateForm } }">
                        <slot name="create-form"
                            :fetchFilter="fetchFilter"
                            :userPermissions="userPermissions"
                            :events="{ closeCreateForm, refetchResults }"
                        ></slot>
                    </template>
                    <template #summaries>
                        <slot name="summaries"></slot>
                    </template>
                </CustomTableMenu>
            </div>
            <div class="flex w-full h-full bg-gray-200">
                <Transition name="slide-fade-left-panel"
                    enter-active-class="transition duration-400 ease-out"
                    enter-from-class="transform translate-x-100 opacity-0"
                    enter-to-class="transform scale-200 opacity-200"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform opacity-200"
                    leave-to-class="transform -translate-x-100 opacity-0"
                >
                    <div
                        v-show="visibleControlPanel"
                        class="sidebar-container left-sidebar sticky top-0"
                        key="filters-panel"
                    >
                        <ControlPanel
                            :filters="filters"
                            :selectedResults="selectedResults"
                            :selectedResultFields="selectedResultFields"
                            :bulkOperations="bulkOperations"
                            :summaryActions="summaryActions"
                            :userPermissions="userPermissions"
                            :isLoading="isLoading"
                            :width="controlPanelWidth"
                            :fetchFilter="fetchFilter"
                            :default-filter-tab="defaultFilterTab"
                            :resultCount="count"
                            :panes="controlPanelPanes"
                            :bulkOperationResponse="bulkOperationResponse"
                            :reorderBulkOperations="reorderBulkOperations"
                            :reorderFilters="reorderFilters"
                            :activeFilters="activeFilters"
                            :summaryButtonResetId="summaryButtonResetId"
                            @refetch-results="refetchResults(fetchFilter)"
                            @clear-selected-results="clearSelectedResults"
                            @set-filter-options="setFilterOptions"
                            @close="hideBulkOperations"
                            @perform-bulk-action="performBulkAction"
                            @perform-summary-action="performSummaryAction"
                            @toggle-pinned-filter="togglePinnedFilter"
                            @toggle-pinned-bulk-operation="togglePinnedBulkOperation"
                            @change-active-filters="updateActiveFilters"
                            @change-filters="handleFilterChange"
                            @handle-bulk-operation-input-change="handleBulkOperationInputChange"
                            @save-default-filters="saveDefaultFilters"
                        />
                    </div>
                </Transition>
                <div class="flex-grow overflow-x-auto h-full w-full">
                    <CustomTableTemplate
                        :visible-control-panel="visibleControlPanel"
                        :control-panel-width="controlPanelWidth"
                    >
                        <template #table-header>
                            <th
                                v-if="menuOptions.selectAllHeader && !menuOptions.expandAllHeader"
                                class="p-0 h-6 w-[50px] sticky -top-[1px] border-t border-b-2 border-r border-gray-300"
                            >
                                <div class="bg-gray-150 h-full flex justify-center p-2 border-gray-300">
                                    <SelectAll
                                        :selectAll="selectAll"
                                        @toggle-select-all="toggleSelectAll"
                                    />
                                </div>
                            </th>
                            <th
                                v-else-if="menuOptions.expandAllHeader"
                                class="p-0 h-[30px] w-[60px] sticky -top-[1px] border-t border-b-2 border-r border-gray-300"
                            >
                                <div class="flex justify-between items-center pl-3 pr-[11px] bg-gray-150 border-gray-300 h-[30px]">
                                    <div v-if="menuOptions.selectAllHeader" class="bg-gray-150 h-full flex justify-center ">
                                        <SelectAll
                                            :selectAll="selectAll"
                                            @toggle-select-all="toggleSelectAll"
                                        />
                                    </div>
                                    <div v-if="menuOptions.expandAllHeader" class="flex flex-col h-[18px] w-[18px] rounded justify-center items-center hover:bg-gray-300 cursor-pointer">
                                        <div v-if="expandAll=='expand'" class="w-full h-full flex items-center justify-center" @click="setExpandAll('collapse')"><ChevronUpMiniSVG /></div>
                                        <div v-else class="w-full h-full flex items-center justify-center" @click="setExpandAll('expand')"><ChevronDownMiniSVG /></div>
                                    </div>
                                </div>
                            </th>
                            <TableHeaderSticky 
                                v-for="tableHeader in tableHeaders"
                                :key="tableHeader.value"
                                :tableHeader="tableHeader"
                                :orderBy="orderBy"
                                @change-ordering="changeOrdering"
                                class="border-r border-gray-300"
                            >
                            </TableHeaderSticky>
                        </template>
                        <TransitionGroup name="results" tag="tbody">
                            <template #table-body>
                                <template v-if="isLoading">
                                    <tr v-for="rowIndex in 50" :key="rowIndex" class="odd:bg-gray-100 even:bg-gray-200 animate-pulse">
                                        <td v-for="cellIndex in cellCount" :key="cellIndex" class=""  :class="rowLoadingClass"></td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <TableRow
                                        v-for="(row, index) in formRows"
                                        :key="row.key"
                                    >
                                        <slot name="row-form"
                                            :index="index"
                                            :fetchFilter="fetchFilter"
                                            :userPermissions="userPermissions"
                                            :selectAll="selectAll"
                                            :clearedSelectedResults="clearedSelectedResults"
                                            :clearedSelectedResultsOnPage="clearedSelectedResultsOnPage"
                                            :results="filteredResults"
                                            :events="{ addSelectedResult, removeSelectedResult, refetchResults, openRowDetails}"
                                        >
                                        </slot>
                                    </TableRow>
                                    <slot name="table-row"
                                        :selectAll="selectAll"
                                        :expandAll="expandAll"
                                        :fetchFilter="fetchFilter"
                                        :userPermissions="userPermissions"
                                        :results="filteredResults"
                                        :selected-results="selectedResultIds"
                                        :clearedSelectedResults="clearedSelectedResults"
                                        :clearedSelectedResultsOnPage="clearedSelectedResultsOnPage"
                                        :events="{ addSelectedResult, removeSelectedResult, addSelectedResults, removeSelectedResults, refetchResults, openRowDetails }">
                                    </slot>
                                    <tr key="blank">
                                        <td class="h-12 bg-gray-200"></td>
                                    </tr>
                                </template>
                            </template>
                        </TransitionGroup>

                        <template #table-footer>
                            <slot name="table-footer"
                                :viewingFromNbr="viewingFromNbr"
                                :viewingToNbr="viewingToNbr"
                                :count="count"
                                :selectedResults="selectedResults"
                                :totalPages="totalPages"
                                :countIsCapped="countIsCapped"
                                :isPastEnd="isPastEnd"
                                :exactCount="exactCount"
                                :results="results"
                                :next="next"
                                :prev="prev"
                                :page="page"
                                :events="{ getNext, goToFirst, getPrev, goToLast, jumpToPage, setPageSizeFilter, setExactCount }"
                            >
                            </slot>
                        </template>
                    </CustomTableTemplate>
                </div>
                <Transition name="slide-fade-right-panel">
                    <div
                        v-if="visibleBulkOperationsPanel"
                        class="sidebar-container right-sidebar sticky top-0"
                        key="bulk-ops-panel"
                    >
                        <BulkOperationsPanel
                            :selectedResults="selectedResults"
                            :filters="filters"
                            :bulkOperations="bulkOperations"
                            :userPermissions="userPermissions"
                            @refetch-results="refetchResults"
                            @clear-selected-results="clearSelectedResults"
                            @close="hideBulkOperations"
                            @set-bulk-operation-options="setBulkOperationOptions"
                        >
                            <template #bulk-operations="{ events: { hideBulkActionMenu }, bulkAction, selectedResults,  }">
                                <slot name="bulk-operations"
                                    :selectedResults="selectedResults"
                                    :bulkAction="bulkAction"
                                    :fetchFilter="fetchFilter"
                                    :events="{ clearSelectAllClearSelectedResults, refetchResults, hideBulkActionMenu }"
                                ></slot>
                            </template>
                        </BulkOperationsPanel>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
    <div v-else class="w-full h-full">
        <div class="w-full h-full flex flex-col">
            <div class="w-full z-30">
                <CustomTableMenu
                    :moduleName="moduleName"
                    :module-name-singular="moduleNameSingular"
                    :pageFilter="pageSizeFilter"
                    :page="page"
                    :numPages="totalPages"
                    :isLoading="isLoading"
                    :filters="filters"
                    :selectAll="selectAll"
                    :selectedResults="selectedResults"
                    :menuOptions="menuOptions"
                    :userPermissions="userPermissions"
                    :bulkOperations="bulkOperations"
                    :viewingFromNbr="viewingFromNbr"
                    :viewingToNbr="viewingToNbr"
                    :default-page-size="defaultPageSize"
                    :count="count"
                    :orderBy="orderBy"
                    :create-modal-size="createModalSize"
                    :activeFilters="activeFilters"
                    :has-next="!!next"
                    :has-prev="!!prev"
                    :count-is-capped="countIsCapped"
                    :is-past-end="isPastEnd"
                    @refetch-results="refetchResults(fetchFilter)"
                    @get-next="getNext"
                    @go-to-first="goToFirst"
                    @get-prev="getPrev"
                    @go-to-last="goToLast"
                    @jump-to-page="jumpToPage"
                    @set-page-size-filter="setPageSizeFilter"
                    @clear-filter="clearFilter"
                    @change-filters="changeFilters"
                    @set-filter-options="setFilterOptions"
                    @toggle-select-all="toggleSelectAll"
                    @clear-select-all="clearSelectAll"
                    @add-form-row="addFormRow"
                    @change-ordering="setOrder"
                    @do-quick-bulk="executeQuickBulkAction"
                    @select-tab="selectTab"
                    @toggle-bulk-operations-panel="toggleBulkOperationsPanel"
                    @toggle-control-panel="toggleControlPanel"
                    @change-active-filters="updateActiveFilters"
                    @clear-all-active-filters="clearAllActiveFilters"
                    @export-to-csv="exportToCSV"
                >
                    <template #bulk-operations="{ events: { hideBulkActionMenu, hideBulkOperations },  bulkAction, selectedResults }">
                        <slot name="bulk-operations"
                            :selectedResults="selectedResults"
                            :bulkAction="bulkAction"
                            :fetchFilter="fetchFilter"
                            :events="{ hideBulkActionMenu, hideBulkOperations, clearSelectAllClearSelectedResults, refetchResults }"
                        ></slot>
                    </template>
                    <template #create-form="{ events: { closeCreateForm } }">
                        <slot name="create-form"
                            :fetchFilter="fetchFilter"
                            :userPermissions="userPermissions"
                            :events="{ closeCreateForm, refetchResults }"
                        ></slot>
                    </template>
                    <template #summaries>
                        <slot name="summaries"></slot>
                    </template>
                </CustomTableMenu>
            </div>
            <div class="flex w-full h-full bg-gray-200">
                <Transition name="slide-fade-left-panel"
                    enter-active-class="transition duration-400 ease-out"
                    enter-from-class="transform translate-x-100 opacity-0"
                    enter-to-class="transform scale-200 opacity-200"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform opacity-200"
                    leave-to-class="transform -translate-x-100 opacity-0"
                >
                    <div
                        v-show="visibleControlPanel"
                        class="sidebar-container left-sidebar sticky top-0"
                        key="filters-panel"
                    >
                        <ControlPanel
                            :filters="filters"
                            :selectedResults="selectedResults"
                            :selectedResultFields="selectedResultFields"
                            :bulkOperations="bulkOperations"
                            :summaryActions="summaryActions"
                            :userPermissions="userPermissions"
                            :isLoading="isLoading"
                            :width="controlPanelWidth"
                            :fetchFilter="fetchFilter"
                            :default-filter-tab="defaultFilterTab"
                            :resultCount="count"
                            :panes="controlPanelPanes"
                            :bulkOperationResponse="bulkOperationResponse"
                            :reorderBulkOperations="reorderBulkOperations"
                            :reorderFilters="reorderFilters"
                            :activeFilters="activeFilters"
                            :summaryButtonResetId="summaryButtonResetId"
                            @refetch-results="refetchResults(fetchFilter)"
                            @clear-selected-results="clearSelectedResults"
                            @set-filter-options="setFilterOptions"
                            @close="hideBulkOperations"
                            @perform-bulk-action="performBulkAction"
                            @perform-summary-action="performSummaryAction"
                            @toggle-pinned-filter="togglePinnedFilter"
                            @toggle-pinned-bulk-operation="togglePinnedBulkOperation"
                            @change-active-filters="updateActiveFilters"
                            @change-filters="handleFilterChange"
                            @handle-bulk-operation-input-change="handleBulkOperationInputChange"
                            @save-filter-order="saveFilterOrder"
                            @save-default-filters="saveDefaultFilters"
                            @save-bulk-operation-order="saveBulkOperationOrder"
                            @set-bulk-operation-options="setBulkOperationOptions"
                        />
                    </div>
                </Transition>
                <div class="flex-grow overflow-x-auto h-full" :class="tableContainerClass">
                    <CustomTableTemplate
                        :visible-control-panel="visibleControlPanel"
                        :control-panel-width="controlPanelWidth"
                    >
                        <template #table-header>
                            <th
                                v-if="menuOptions.selectAllHeader && !menuOptions.expandAllHeader"
                                class="p-0 h-6 w-[50px] sticky -top-[1px] border-t border-b-2 border-r border-gray-300"
                            >
                                <div class="bg-gray-150 h-full flex justify-center p-2 border-gray-300">
                                    <SelectAll
                                        :selectAll="selectAll"
                                        @toggle-select-all="toggleSelectAll"
                                    />
                                </div>
                            </th>
                            <th
                                v-else-if="menuOptions.expandAllHeader"
                                class="p-0 h-[30px] w-[60px] sticky -top-[1px] border-t border-b-2 border-r border-gray-300"
                            >
                                <div class="flex justify-between items-center pl-3 pr-[11px] bg-gray-150 border-gray-300 h-[30px]">
                                    <div v-if="menuOptions.selectAllHeader" class="bg-gray-150 h-full flex justify-center ">
                                        <SelectAll
                                            :selectAll="selectAll"
                                            @toggle-select-all="toggleSelectAll"
                                        />
                                    </div>
                                    <div v-if="menuOptions.expandAllHeader" class="flex flex-col h-[18px] w-[18px] rounded justify-center items-center hover:bg-gray-300 cursor-pointer">
                                        <div v-if="expandAll=='expand'" class="w-full h-full flex items-center justify-center" @click="setExpandAll('collapse')"><ChevronUpMiniSVG /></div>
                                        <div v-else class="w-full h-full flex items-center justify-center" @click="setExpandAll('expand')"><ChevronDownMiniSVG /></div>
                                    </div>
                                </div>
                            </th>
            
                            <TableHeaderSticky 
                                v-for="tableHeader in tableHeaders"
                                :key="tableHeader.value"
                                :tableHeader="tableHeader"
                                :orderBy="orderBy"
                                @change-ordering="changeOrdering"
                                class="border-r border-gray-300"
                            >
                            </TableHeaderSticky>
                        </template>
                        <template #table-body>
                            <template v-if="isLoading">
                            <tr v-for="rowIndex in pageSizeFilter.pageSizeFilter" :key="rowIndex" class="odd:bg-gray-100 even:bg-gray-200 animate-pulse">
                                <td v-for="cellIndex in cellCount" :key="cellIndex" class=""  :class="rowLoadingClass"></td>
                            </tr>
                            </template>
                            <template v-else>
                                <TableRow
                                    v-for="(row, index) in formRows"
                                    :key="row.key"
                                >
                                    <slot name="row-form"
                                        :index="index"
                                        :fetchFilter="fetchFilter"
                                        :userPermissions="userPermissions"
                                        :selectAll="selectAll"
                                        :clearedSelectedResults="clearedSelectedResults"
                                        :clearedSelectedResultsOnPage="clearedSelectedResultsOnPage"
                                        :results="filteredResults"
                                        :events="{ addSelectedResult, removeSelectedResult, refetchResults, openRowDetails}"
                                    >
                                    </slot>
                                </TableRow>
                                <slot name="table-row"
                                    :selectAll="selectAll"
                                    :expandAll="expandAll"
                                    :fetchFilter="fetchFilter"
                                    :userPermissions="userPermissions"
                                    :results="filteredResults"
                                    :selected-results="selectedResultIds"
                                    :clearedSelectedResults="clearedSelectedResults"
                                    :clearedSelectedResultsOnPage="clearedSelectedResultsOnPage"
                                    :openRow="openRow"
                                    :events="{ addSelectedResult, removeSelectedResult, addSelectedResults, removeSelectedResults, refetchResults, openRowDetails }">
                                </slot>
                                <tr key="blank">
                                    <td class="h-12 bg-gray-200"></td>
                                </tr>
                            </template>
                        </template>
                        <template #table-footer>
                            <slot name="table-footer"
                                :viewingFromNbr="viewingFromNbr"
                                :viewingToNbr="viewingToNbr"
                                :count="count"
                                :selectedResults="selectedResults"
                                :totalPages="totalPages"
                                :countIsCapped="countIsCapped"
                                :isPastEnd="isPastEnd"
                                :exactCount="exactCount"
                                :results="results"
                                :next="next"
                                :prev="prev"
                                :page="page"
                                :events="{ getNext, goToFirst, getPrev, goToLast, jumpToPage, setPageSizeFilter, setExactCount }"
                            >
                            </slot>
                        </template>
                    </CustomTableTemplate>
                </div>
                <Transition name="slide-fade-right-panel">
                    <slot
                        name="details-page"
                        :resultRow="openRow"
                        :userPermissions="userPermissions"
                        :fetchFilter="fetchFilter"
                        :events="{ closeDetails, refetchResults }"
                    ></slot>
                </Transition>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, reactive, defineProps, defineEmits, computed, watch, shallowReactive, onMounted} from 'vue';
import CustomTableMenu from './CustomTableMenu.vue';
import CustomTableTemplate from './CustomTableTemplate.vue';
import TableHeaderSticky from './TableHeaderSticky.vue';
import TableRow from './TableRow.vue';
import SelectAll from './SelectAll.vue';
import BulkOperationsPanel from './BulkOperationsPanel.vue';
import ChevronDownMiniSVG from '../svg-components/ChevronDownMiniSVG.vue';
import ChevronUpMiniSVG from '../svg-components/ChevronUpMiniSVG.vue';
import ControlPanel from './ControlPanel.vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';

const store = useStore();
const $toast = useToast();

const props = defineProps([
    'response',
    'moduleName',
    'moduleNameSingular',
    'isLoading',
    'tableHeaders',
    'filters',
    'menuOptions',
    'bulkOperations',
    'summaryActions',
    'defaultOrdering',
    'defaultPageSize',
    'shouldReload',
    'shouldClearSelected',
    'bulkOperationResponse',
    'reorderBulkOperations',
    'reorderFilters',
    'firstHeaderStyling',
    'createModalSize',
    'selectedResultFields',
    'summaryButtonResetId',
    'defaultFilterTab'
]);

const emit = defineEmits([
    'refetch-results',
    'clear-filter',
    'clear-all-active-filters',
    'change-filters',
    'save-filter-order',
    'save-default-filters',
    'change-ordering',
    'set-filter-options',
    'do-quick-bulk',
    'select-tab',
    'update-control-panel-visibility',
    'perform-bulk-action',
    'perform-summary-action',
    'clear-summary-action-data',
    'toggle-pinned-filter',
    'toggle-pinned-bulk-operation',
    'handle-bulk-operation-input-change',
    'save-bulk-operation-order',
    'export-to-csv',
    'set-bulk-operation-options'
]);

const isLoading = ref(props.isLoading);
const userPermissions = shallowReactive({
    isManagement: null,
    isHourly: null,
    isStudent: null,
    isResearch: null,
    isAdvancedResearch: null,
    isSuperuser: null,
    isAccountManager: null,
    isAccountStaff: null,
    isDev: null,
    isResearchLead: null,
    isAllCompanies: null,
    isGenerateTimecard: null,
});
const awaitingSearch = ref(false);
const shouldRefetchResults = ref(false);
const results = ref([]);
const filteredResults = ref([]);
const next = ref(null);
const prev = ref(null);
const count = ref(null);
// Set when the backend capped its COUNT() for speed — count and totalPages are
// then floors, and paging is driven by `next` alone.
const countIsCapped = ref(false);
// The user want the exact count, so every request from here on pays for a
// full COUNT(*). Unticking it takes effect on the next request
const exactCount = ref(false);
const page = ref(1);
const pageSize = ref(null);
const totalPages = ref(0);
const pageSizeFilter = reactive({
    pageSizeFilter: props.menuOptions && props.menuOptions.defaultPageSize ? props.menuOptions.defaultPageSize : 250
});
const openRowType = ref(props.menuOptions.openRowType ? props.menuOptions.openRowType : 'largeModal')
const openRow = ref(null)
const filters = reactive(props.filters);
const formRows = ref([])
const firstHeader = computed(() => {
    if(props.firstHeaderStyling){
        return props.firstHeaderStyling
    } else {
        return "w-10"
    }
})
const selectedTab = ref(null);
const selectAll = ref(false);
const selectedResults = ref([]);
const clearedSelectedResults = ref(false);
const clearedSelectedResultsOnPage = ref(false);
const quickSearchFilter = ref(null)
const expandAll = ref(null)
const orderBy = ref(props.defaultOrdering ? props.defaultOrdering : []);
const activeFilters = ref([])

const tableContainerClass = ref(props.menuOptions && props.menuOptions.tableClass ? props.menuOptions.tableClass : 'w-full' )

const cellCount = computed(() => {
    if(props.menuOptions.selectAllHeader || props.menuOptions.expandAllHeader) {
        return props.tableHeaders.length + 1
    } else {
        return props.tableHeaders
    }
})

const visibleBulkOperationsPanel = ref(false)
const visibleControlPanel = ref(props.menuOptions && props.menuOptions.controlPanel ? true : false)
const controlPanelWidth = ref(props.menuOptions && props.menuOptions.controlPanel && props.menuOptions.controlPanel.width ? props.menuOptions.controlPanel.width : 'w-[288px]')
const defaultFilterTab = ref(props.menuOptions && props.menuOptions.controlPanel && props.menuOptions.controlPanel.defaultFilterTab ? props.menuOptions.controlPanel.defaultFilterTab : 'all')
const rowLoadingClass = ref(props.menuOptions &&props.menuOptions.loading && props.menuOptions.loading.rowClass ? props.menuOptions.loading.rowClass : 'h-24 bg-inherit border-r border-b border-gray-300')

const openRowRightPanel = computed(() => {
    return openRowType.value == 'rightPanel' && openRow.value!=null
})

watch(() => props.isLoading, () => {
    isLoading.value = props.isLoading
})

watch(() => props.shouldReload, () => {
    // console.log('watcher: props.shouldReload')
    refetchResults(fetchFilter.value)
})

watch(() => props.shouldClearSelected, () => {
    // console.log('watcher: props.shouldClearSelected')
    clearSelectAllClearSelectedResults()
})

watch(() => props.response, () => {
    setResponse(props.response)
})

watch(() => visibleControlPanel.value, () => {
    emit('update-control-panel-visibility', visibleControlPanel.value)
})

function updateActiveFilters(activeFiltersValue) {
    activeFilters.value = activeFiltersValue
}

function toggleBulkOperationsPanel() {
    // console.log('toggleBulkOpsPanel')
    visibleBulkOperationsPanel.value = !visibleBulkOperationsPanel.value
}

function hideBulkOperations() {
    visibleBulkOperationsPanel.value = false
}

function toggleControlPanel() {
    visibleControlPanel.value = !visibleControlPanel.value
}
function setDefaultTab() {
    // console.log('setDefaultTab')
    let defaultTab = null
    if(props.menuOptions && props.menuOptions.tabs) {
        for(const tabKey in props.menuOptions.tabs) {
            if(props.menuOptions.tabs[tabKey] && props.menuOptions.tabs[tabKey].isSelected) {
                defaultTab = tabKey
                break
            }
        }
    }
    selectedTab.value = defaultTab
}

function setExpandAll(expandAllValue) {
    expandAll.value = expandAllValue
}

function refetchResults(filterParams) {
    // console.log('refetchResults')
    // console.log('filter params:')
    // console.log(filterParams)
    // clearSelectAllClearSelectedResults()
    shouldRefetchResults.value = true
    if(shouldRefetchResults.value) {
        emit('refetch-results', filterParams)
    }
}

function handleFilterChange(){
    clearSelectAllClearSelectedResults()
}

function openRowDetails(resultRow) {
    // console.log('customTable: openRowDetails', resultRow)
    openRow.value = resultRow
}

function closeDetails() {
    // console.log('CustomTablePage: closeDetails')
    openRow.value = null
}

function saveFilterOrder() {
    emit('save-filter-order')
}

function saveDefaultFilters(resolve) {
    emit('save-default-filters', resolve)
}

function saveBulkOperationOrder() {
    emit('save-bulk-operation-order')
}

function addSelectedResult(resultRow) {
    let foundIndex = selectedResultIds.value.indexOf(resultRow.id)
    if (foundIndex === -1){
        selectedResults.value.push(resultRow);
    }
    // console.log('selected results', selectedResults.value)
}

function removeSelectedResult(resultRow) {
    let foundIndex = selectedResultIds.value.indexOf(resultRow.id);
  if (foundIndex !== -1) {
    selectedResults.value.splice(foundIndex, 1);
  }
}


function addSelectedResults(resultRows) {
    // console.log('addSelectedResults')
    // console.log(resultRows)
    resultRows.forEach((resultRow) => {
        let foundIndex = selectedResultIds.value.indexOf(resultRow.id)
        if (foundIndex === -1){
            selectedResults.value.push(resultRow);
        }
    })

    // console.log('selected results', selectedResults.value)
}

function removeSelectedResults(resultRows) {
    // console.log('removeSelectedResults')
    // console.log(resultRows)
    resultRows.forEach((resultRow) => {
        let foundIndex = selectedResultIds.value.indexOf(resultRow.id);
        if (foundIndex !== -1) {
            selectedResults.value.splice(foundIndex, 1);
        }
    })
}

function quickSearch(filterValue) {
    // console.log('quickSearch', filterValue)
    if(filterValue) {
        quickSearchFilter.value = filterValue.toLowerCase()
    } else {
        quickSearchFilter.value = null
    }
}

function clearQuickSearch() {
    // console.log('clearQuickSearch')
    quickSearchFilter.value = null
}

function togglePinnedFilter(key) {
    emit('toggle-pinned-filter', key)
}

function togglePinnedBulkOperation(key) {
    emit('toggle-pinned-bulk-operation', key)
}

function filterResultByQuickSearch(resultRow) {
    // console.log('filterResultByQuickSearch', resultRow, props.quickSearchFields)
    if(props.quickSearchFields) {
        for(let i=0; i < props.quickSearchFields.length; i++) {
            if(resultRow[props.quickSearchFields[i]]) {
                // console.log(resultRow[props.quickSearchFields[i]], quickSearchFilter.value)
                if(resultRow[props.quickSearchFields[i]].toLowerCase().includes(quickSearchFilter.value)){
                    return true;
                }
            }
        }
        return false
    }
    return true
}

function setUserPermissions () {
    // console.log('setUserPermissions')
    let userPermissionsStore = store.getters["auth/showUserPermissions"];
    userPermissions.isManagement = userPermissionsStore.isManagement;
    userPermissions.isHourly = userPermissionsStore.isHourly;
    userPermissions.isStudent = userPermissionsStore.isStudent;
    userPermissions.isResearch = userPermissionsStore.isResearch;
    userPermissions.isAdvancedResearch = userPermissionsStore.isAdvancedResearch;
    userPermissions.isSuperuser = userPermissionsStore.isSuperuser;
    userPermissions.isAccountManager = userPermissionsStore.isAccountManager;
    userPermissions.isAccountStaff = userPermissionsStore.isAccountStaff;
    userPermissions.isDev = userPermissionsStore.isDev;
    userPermissions.isResearchLead = userPermissionsStore.isResearchLead;
    userPermissions.isAllCompanies = userPermissionsStore.isAllCompanies;
    userPermissions.isGenerateTimecard = userPermissionsStore.isGenerateTimecard;
}

const fetchFilter = computed(() => {
    let query = new URLSearchParams({});
    for(const filter in filters) {
        if(filters[filter].min || filters[filter].max) {
            if(filters[filter].min.value) {
                query.append(filters[filter].min.name, filters[filter].min.value)
                // if(filters[filter].expression) {
                //     query.append(filters[filter].expression.name, filters[filter].expression.value)
                // }
            }
            if(filters[filter].max.value) {
                query.append(filters[filter].max.name, filters[filter].max.value)
                // if(filters[filter].expression) {
                //     query.append(filters[filter].expression.name, filters[filter].expression.value)
                // }
            }
        } else {
            if(filters[filter].multiSelect) {
                if(filters[filter].value.length > 0) {
                    filters[filter].value.forEach(filterValue => {
                        query.append(filters[filter].name, filterValue)
                    })
                    if(filters[filter].expression) {
                        query.append(filters[filter].expression.name, filters[filter].expression.value)
                    }
                }
            } else {
                if(filters[filter].value) {
                    query.append(filters[filter].name, filters[filter].value)
                    if(filters[filter].expression) {
                        query.append(filters[filter].expression.name, filters[filter].expression.value)
                    }
                }
            }
        }
        // console.log('filter')
        // console.log(filter)

    }
    if(pageSizeFilter.pageSizeFilter) {
      query.append("page_size", pageSizeFilter.pageSizeFilter);
    }
    if(page.value) {
        query.append('page', page.value)
    }
    if(exactCount.value) {
        query.append('exact_count', 'true')
    }
    if(orderBy.value.length > 0){
      query.append("order_by", orderBy.value.join(','))
    }
    let url;
    let queriesCount = 0;
    for (let value of query.values()) {
        queriesCount = queriesCount + 1;
    }
    if (queriesCount > 0) {
        url = "?" + query.toString();
    } else {
        url = "?";
    }
    return url
})

onMounted(() => {
    if(props.menuOptions && props.menuOptions.fetchResultsOnMount) {
        refetchResults(fetchFilter.value)
    }
})

const tableHeadersLastIndex = computed(() => {
    if(props.tableHeaders) {
        return props.tableHeaders.length - 1
    } else {
        return 0
    }
})

const controlPanelPanes = computed(() => {
    if(props.menuOptions && props.menuOptions.controlPanel && props.menuOptions.controlPanel.panes) {
        return props.menuOptions.controlPanel.panes
    }
    return []
})

const selectedResultIds = computed(() => {
    if(selectedResults.value.length > 0) {
        return selectedResults.value.map(({ id }) => {
            return id;
        });
    } else {
        return [];
    }
})

const viewingFromNbr = computed(function () {
    // console.log(page.value)
    // console.log(pageSizeFilter.pageSizeFilter)
    if (pageSize.value === 0) return 0
    return (page.value * pageSizeFilter.pageSizeFilter + 1) - pageSizeFilter.pageSizeFilter
  })
  
  
// A capped count can't tell us where the list ends, so nothing stops a jump to
// page 400 of a 30-page list. The page just comes back empty — flag that so the
// footer can say so instead of printing a backwards range.
const isPastEnd = computed(function () {
    return page.value != null && page.value > 1 && pageSize.value === 0
})

const viewingToNbr = computed(function () {
// Unpaginated responses put every row on screen.
if (page.value == null || pageSize.value == null) {
    return count.value;
}
// Span from the first row of this page rather than assuming every page is
// full — the last one rarely is. `count` can't stand in for the end of the
// last page either: the backend caps it.
return pageSize.value ? viewingFromNbr.value + pageSize.value - 1 : 0
})

function performBulkAction(bulkActionData) {
    emit('perform-bulk-action', bulkActionData)
}

function performSummaryAction(summaryActionData) {
    emit('perform-summary-action', summaryActionData)
}

function handleBulkOperationInputChange(bulkAction, inputKey, inputValue) {
    emit('handle-bulk-operation-input-change', bulkAction, inputKey, inputValue)
}

function getNext() {
if (next.value) {
    refetchResults(fetchFilter.value + next.value)
      clearSelectAllClearSelectedResults();
} else {
    alert("No next page")
}
}
  
function getPrev() {
if (prev.value) {
    refetchResults(fetchFilter.value + prev.value)
      clearSelectAllClearSelectedResults();
} else {
    alert("No prev page")
}
}

function jumpToPage(targetPage) {
    refetchResults(fetchFilter.value + `&page=${targetPage}`)
      clearSelectAllClearSelectedResults();
}

function goToFirst(){
    refetchResults(fetchFilter.value + `&page=${1}`)
      clearSelectAllClearSelectedResults();
}
  
function goToLast(){
    refetchResults(fetchFilter.value + `&page=${totalPages.value}`)
      clearSelectAllClearSelectedResults();
}
function setExactCount(value) {
    exactCount.value = value
    // Unticking costs nothing — the next request of any kind simply omits the param.
    if(!value) return
    // A capped count let the user page past the real end, where an exact count
    // would 404. `page=last` lands them on the real last page instead — the
    // backend resolves it against the exact total in this same request.
    if(isPastEnd.value) {
        refetchResults(fetchFilter.value + '&page=last')
        clearSelectAllClearSelectedResults();
    } else {
        // Same rows, same page — only the total changes, so selections stand.
        refetchResults(fetchFilter.value)
    }
}

function setPageSizeFilter(target=null){
    // console.log(target)
    if(target) {
      target == "clear" ? (pageSizeFilter.pageSizeFilter = 50) : (pageSizeFilter.pageSizeFilter = target);
      refetchResults(fetchFilter.value + `&page_size=${pageSizeFilter.pageSizeFilter}`)
    }
      clearSelectAllClearSelectedResults();
}

function toggleSelectAll() {
    if(selectAll.value) {
        clearSelectAllClearSelectedResults()
    } else {
        selectAll.value = true;
    }
}

function clearSelectAllClearSelectedResults() {
    clearSelectAll();
    clearSelectedResults();
}

function clearSelectAll() {
  if (selectAll.value) {
    selectAll.value = false;
  }
}

function clearSelectedResults() {
    // console.log('CustomTable: clearSelectedResults()')
    selectedResults.value = []
    clearedSelectedResults.value = !clearedSelectedResults.value;
}

function clearFilter(filterName) {
    emit('clear-filter', filterName)
}

function clearAllActiveFilters() {
    emit('clear-all-active-filters')
}

function changeFilters(filters) {
    console.log('filters changed')
    clearSelectAllClearSelectedResults()
    emit('change-filters', filters)
}

function setFilterOptions(filterName) {
    // console.log('CustomTable: setFilterOptions', filterName)
    emit('set-filter-options', filterName)
}

function setBulkOperationOptions(bulkOperationKey) {
    emit('set-bulk-operation-options', bulkOperationKey)
}

function exportToCSV(resultType) {
    console.log('CustomTable: exportToCSV', resultType)
    let payload = null;
    if(resultType=='all') {
        payload = fetchFilter.value
    } else if (resultType=='selected') {
        payload = selectedResultIds
    }
    emit('export-to-csv', payload)
}

function selectTab(tabKey) {
    selectedTab.value = tabKey
    emit('select-tab', tabKey)
    if(props.menuOptions.tabChangeClearsSelected) {
        clearSelectAllClearSelectedResults()
    }
}

function getFilteredResults(resetCount) {
    // console.log('getFilteredResults')
    if(selectedTab.value) {
        if(props.menuOptions.tabs[selectedTab.value].handleTabSelect) {
            filteredResults.value = props.menuOptions.tabs[selectedTab.value].handleTabSelect(results.value)
        }
    } else {
        filteredResults.value = results.value
    }
    if(quickSearchFilter.value) {
        filteredResults.value = filteredResults.value.filter(filterResultByQuickSearch)
    }
    if(resetCount) {
        count.value = filteredResults.value.length
    }
}

function handleRowOpening() {
    // console.log('handleRowOpening', filteredResults.value.length, props.menuOptions.openRowIndexOnLoad)
    if(props.menuOptions.openRowIndexOnLoad != undefined && filteredResults.value.length > props.menuOptions.openRowIndexOnLoad) {
        openRowDetails(filteredResults.value[props.menuOptions.openRowIndexOnLoad])
        if(filteredResults.value.includes(row => row.id == openRow.value.id)) {
            openRowDetails(filteredResults.value.find(row => row.id == openRow.value.id))
        } else {
            openRowDetails(filteredResults.value[props.menuOptions.openRowIndexOnLoad])
        }
    } 
}

function addFormRow(){
    formRows.value.push(
        {key: Math.random()}
    )
}

function closeRowForm(index) {
    formRows.value.splice(index, 1)
}

function noOrdering(fieldName) {
  if(!orderBy.value.includes(fieldName) && !orderBy.value.includes('-' + fieldName)){
    return true
  } else {
    return false
  }
}

function orderedDescending(fieldName){
  if(orderBy.value.includes('-' + fieldName)){
    return true
  } else {
    return false
  }  
}

function orderedAscending(fieldName){
  if(orderBy.value.includes(fieldName)){
    return true
  } else {
    return false
  }  
}

function changeOrdering(fieldName) {
  if(orderedAscending(fieldName)){
    const index = orderBy.value.indexOf(fieldName)
    orderBy.value.splice(index, 1)
  } else if(orderedDescending(fieldName)) {
    const index = orderBy.value.indexOf('-' + fieldName)
    orderBy.value[index] = fieldName
  } else {
    orderBy.value.push('-' + fieldName)
  }
}

function clearSort() {
  orderBy.value = []
}

function setOrder(fieldNames) {
  orderBy.value = fieldNames
}

function executeQuickBulkAction(bulkActionName) {
    emit('do-quick-bulk', bulkActionName, selectedResults.value)
}

function setResponse(response){
    if(response) {
        // console.log('response')
        // console.log(response)
        // console.log(response.status)
        if(response.status == 200) {
            let resetCount = false
            if(response.data.results) {
                // paginated
                results.value = response.data.results
                // filteredResults.value = response.data.results
                next.value = response.data.next ? `&page=${response.data.page + 1}` : null
                prev.value = response.data.previous ? `&page=${response.data.page - 1}` : null
                count.value = response.data.count
                countIsCapped.value = !!response.data.count_is_capped
                page.value = response.data.page
                pageSize.value = response.data.results.length
                totalPages.value = response.data.num_pages
            } else {
                // not paginated
                results.value = response.data
                // filteredResults.value = quickSearchFilter.value ? response.data.filter(filterResultByQuickSearch) : response.data
                next.value = null
                prev.value = null
                count.value = response.data.length
                countIsCapped.value = false
                page.value = null
                pageSize.value = null
                totalPages.value = null
                resetCount = true
            }
            getFilteredResults(resetCount)
            handleRowOpening()
        } else {
            $toast.open({
                message: `Error receiving ${props.moduleName} results.`,
                type: 'error',
                duration: 5000
            })
        }
    }
}



watch(() => Object.values(filters).map((filter) => filter.value).toString(), () => {
    // console.log('watch: filters', filters)
    if(!awaitingSearch.value) {
        shouldRefetchResults.value = false
        setTimeout(() => {
            page.value = 1
            refetchResults(fetchFilter.value);
            emit('clear-summary-action-data')
            awaitingSearch.value = false
        }, 1500)
    }
    awaitingSearch.value = true
})

watch(() => Object.values(filters).filter(filter => filter.min).map((filter) => filter.min.value).toString(), () => {
    // console.log('watch: filters', filters)
    if(!awaitingSearch.value) {
        shouldRefetchResults.value = false
        setTimeout(() => {
            page.value = 1
            refetchResults(fetchFilter.value);
            emit('clear-summary-action-data')
            awaitingSearch.value = false
        }, 1500)
    }
    awaitingSearch.value = true
})

watch(() => Object.values(filters).filter(filter => filter.max).map((filter) => filter.max.value).toString(), () => {
    // console.log('watch: filters', filters)
    if(!awaitingSearch.value) {
        shouldRefetchResults.value = false
        setTimeout(() => {
            page.value = 1
            refetchResults(fetchFilter.value);
            emit('clear-summary-action-data')
            awaitingSearch.value = false
        }, 1500)
    }
    awaitingSearch.value = true
})

watch(() => orderBy.value.toString(), () => {
    if(!awaitingSearch.value) {
        shouldRefetchResults.value = false
          setTimeout(() => {
            page.value = 1
            refetchResults(fetchFilter.value);
            awaitingSearch.value = false;
          }, 1500);
        }
        awaitingSearch.value = true
})

setUserPermissions()
setResponse(props.response)
setDefaultTab()
</script>

<style scoped>
.results-move,
.results-enter-active {
    transition: all 0.3s ease;
}
.results-leave-active {
  transition: all 0.1s ease;
}
.results-enter-from,
.results-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.results-leave-active {
    position: absolute;
    /* position: relative; */
}

/* Define a variable for sidebar width for consistency */
:root {
  --sidebar-width: 280px; /* Adjust to your desired sidebar width, e.g., Tailwind's w-72 */
  --sidebar-transition-duration: 0.3s; /* Adjust duration */
}

.sidebar-container {
  width: var(--sidebar-width);
  max-width: var(--sidebar-width); /* Initial state when open */
  overflow: hidden; /* Crucial for content during max-width transition */
  flex-shrink: 0; /* Prevent flexbox from shrinking it prematurely */
  /* Add any other common styles for your sidebars: padding, background, etc. */
  /* e.g., padding: 1rem; */
}

/* Left Sidebar Transition (slide-fade-left-panel) */
.slide-fade-left-panel-enter-active,
.slide-fade-left-panel-leave-active {
  transition-property: transform, max-width, opacity;
  transition-duration: var(--sidebar-transition-duration);
  transition-timing-function: ease-out;
}

.slide-fade-left-panel-enter-from,
.slide-fade-left-panel-leave-to {
  transform: translateX(-100%);
  max-width: 0;
  opacity: 0;
}

.slide-fade-left-panel-enter-to,
.slide-fade-left-panel-leave-from {
  transform: translateX(0);
  max-width: var(--sidebar-width);
  opacity: 1;
}

/* Right Sidebar Transition (slide-fade-right-panel) */
.slide-fade-right-panel-enter-active,
.slide-fade-right-panel-leave-active {
  transition-property: transform, max-width, opacity;
  transition-duration: var(--sidebar-transition-duration);
  transition-timing-function: ease-out;
}

.slide-fade-right-panel-enter-from,
.slide-fade-right-panel-leave-to {
  transform: translateX(100%);
  max-width: 0;
  opacity: 0;
}

.slide-fade-right-panel-enter-to,
.slide-fade-right-panel-leave-from {
  transform: translateX(0);
  max-width: var(--sidebar-width);
  opacity: 1;
}



/* Ensure your table container can grow */
/* If CustomTableTemplate's root is not already flex-grow, you might need: */
/* For example, if CustomTableTemplate's root element is a div: */
/*
:deep(.custom-table-template-root-class) {
  flex-grow: 1;
}
*/
/* Or simply add flex-grow to the wrapper div as shown in the template above */
</style>